/**
 * patientTimelinePdf — Genera un PDF profesional de una página con el
 * timeline cronológico del paciente usando jsPDF + autoTable.
 *
 * Diseño:
 *   • Encabezado tipo "ficha Apple" con marca ConsuDoctor, nombre, documento,
 *     edad, teléfono, email y fecha de generación.
 *   • Chip rojo con alergias (si existen).
 *   • Tabla con columnas: Fecha | Tipo | Detalle.
 *   • Footer discreto con número de página.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const TYPE_LABEL = {
  appt: "Cita",
  rx: "Receta",
  dx: "Diagnóstico",
  lab: "Laboratorio",
};

const TYPE_COLOR = {
  appt: [219, 234, 254], // azul suave
  rx: [220, 252, 231], // verde suave
  dx: [254, 243, 199], // amarillo suave
  lab: [243, 232, 255], // violeta suave
};

const TYPE_TEXT_COLOR = {
  appt: [29, 78, 216],
  rx: [21, 128, 61],
  dx: [180, 83, 9],
  lab: [126, 34, 206],
};

function calcAge(birthDate) {
  if (!birthDate) return "—";
  const b = new Date(birthDate);
  if (Number.isNaN(b.getTime())) return "—";
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return `${age} años`;
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export function generateTimelinePdf({ patient, timeline, doctor }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 40;

  // ── Encabezado ─────────────────────────────────────────────
  // Marca
  doc.setFillColor(0, 113, 227); // #0071E3
  doc.roundedRect(marginX, 36, 28, 28, 6, 6, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("C", marginX + 14, 55, { align: "center" });

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("ConsuDoctor", marginX + 38, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text("Historial clínico del paciente", marginX + 38, 62);

  // Fecha de generación (derecha)
  const genAt = new Date().toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  doc.setFontSize(8.5);
  doc.setTextColor(120, 120, 120);
  doc.text(`Generado: ${genAt}`, pageWidth - marginX, 50, { align: "right" });
  if (doctor?.name) {
    doc.text(`Dr(a). ${doctor.name}`, pageWidth - marginX, 62, {
      align: "right",
    });
  }

  // Separador fino
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.5);
  doc.line(marginX, 80, pageWidth - marginX, 80);

  // ── Ficha del paciente ─────────────────────────────────────
  let y = 100;
  doc.setFont("helvetica", "bold");
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(20);
  doc.text(patient.full_name || "Paciente", marginX, y);
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const metaParts = [
    calcAge(patient.birth_date),
    patient.gender === "F" ? "Femenino" : patient.gender === "M" ? "Masculino" : null,
    patient.blood_type ? `Sangre ${patient.blood_type}` : null,
    patient.document_id ? `Doc: ${patient.document_id}` : null,
  ].filter(Boolean);
  doc.text(metaParts.join("  ·  "), marginX, y);
  y += 20;

  // Contacto
  const contactParts = [
    patient.phone ? `Tel: ${patient.phone}` : null,
    patient.email ? `Email: ${patient.email}` : null,
    patient.address ? `Dir: ${patient.address}` : null,
  ].filter(Boolean);
  if (contactParts.length > 0) {
    doc.setFontSize(9.5);
    doc.setTextColor(80, 80, 80);
    doc.text(contactParts.join("    "), marginX, y, {
      maxWidth: pageWidth - marginX * 2,
    });
    y += 14;
  }

  // Alergias (si existen)
  const allergies = (patient.allergies || "")
    .split(/[,;·]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (allergies.length > 0) {
    y += 6;
    doc.setFillColor(254, 243, 242);
    doc.setDrawColor(254, 228, 226);
    doc.setLineWidth(0.5);
    doc.roundedRect(marginX, y, pageWidth - marginX * 2, 24, 6, 6, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(180, 35, 24);
    doc.text("ALERGIAS", marginX + 10, y + 11);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.text(allergies.join(" · "), marginX + 62, y + 11, {
      maxWidth: pageWidth - marginX * 2 - 70,
    });
    y += 32;
  } else {
    y += 6;
  }

  // Condiciones (si existen)
  const conditions = (patient.notes || "")
    .split(/[,;·\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (conditions.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(80, 80, 80);
    doc.text("HISTORIAL MÉDICO", marginX, y);
    y += 10;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    doc.text(conditions.join(" · "), marginX, y, {
      maxWidth: pageWidth - marginX * 2,
    });
    y += 20;
  }

  // ── Timeline (tabla) ───────────────────────────────────────
  const rows = (timeline || []).map((e) => [
    fmt(e.date),
    TYPE_LABEL[e.type] || "—",
    `${e.title}${e.subtitle && e.subtitle !== "—" ? "\n" + e.subtitle : ""}`,
  ]);

  autoTable(doc, {
    startY: y + 4,
    head: [["Fecha", "Tipo", "Detalle"]],
    body: rows.length > 0 ? rows : [["—", "—", "Sin eventos registrados"]],
    margin: { left: marginX, right: marginX },
    styles: {
      font: "helvetica",
      fontSize: 9.5,
      cellPadding: { top: 8, right: 8, bottom: 8, left: 10 },
      textColor: [40, 40, 40],
      lineColor: [240, 240, 240],
      lineWidth: 0.5,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [245, 245, 247],
      textColor: [100, 100, 100],
      fontSize: 8,
      fontStyle: "bold",
      halign: "left",
      cellPadding: { top: 8, right: 8, bottom: 8, left: 10 },
    },
    alternateRowStyles: { fillColor: [252, 252, 253] },
    columnStyles: {
      0: { cellWidth: 85, textColor: [100, 100, 100], fontSize: 9 },
      1: { cellWidth: 70, fontStyle: "bold" },
      2: { cellWidth: "auto" },
    },
    didParseCell: (data) => {
      // Pinta la celda "Tipo" con color según el tipo del evento
      if (data.section === "body" && data.column.index === 1 && timeline[data.row.index]) {
        const t = timeline[data.row.index].type;
        if (TYPE_COLOR[t]) {
          data.cell.styles.fillColor = TYPE_COLOR[t];
          data.cell.styles.textColor = TYPE_TEXT_COLOR[t];
        }
      }
    },
  });

  // ── Footer con paginación ──────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(160, 160, 160);
    doc.text(
      `ConsuDoctor · Historial clínico — Confidencial`,
      marginX,
      pageHeight - 20
    );
    doc.text(`Página ${i} de ${pageCount}`, pageWidth - marginX, pageHeight - 20, {
      align: "right",
    });
  }

  // Nombre de archivo seguro
  const safeName = (patient.full_name || "paciente")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
  doc.save(`timeline_${safeName}_${Date.now()}.pdf`);
}
